import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Award, Clock, TrendingUp, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, services, testimonials, certifications } from '../data/mock';

const Home = () => {
  const benefits = [
    {
      icon: Clock,
      title: "Save Time",
      description: "Focus on running your business while we handle the numbers."
    },
    {
      icon: Shield,
      title: "Stay Compliant",
      description: "MTD-ready and HMRC registered. Your business stays on the right side of regulations."
    },
    {
      icon: TrendingUp,
      title: "Gain Clarity",
      description: "Clear financial reports that help you make informed decisions."
    },
    {
      icon: Users,
      title: "Personal Service",
      description: "Dedicated support from a real person who understands your business."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal/5 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-teal blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-amber blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white border border-teal/20 rounded-full px-4 py-2 mb-6">
              <CheckCircle className="text-teal" size={16} />
              <span className="text-sm font-medium text-navy">QuickBooks Pro Advisor • MTD Ready • AML Compliant</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-tight mb-6">
              Bookkeeping That Brings
              <span className="text-teal"> Peace of Mind</span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Professional, accurate bookkeeping for small businesses, freelancers, and entrepreneurs. 
              We handle your finances so you can focus on what you do best—growing your business.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-amber hover:bg-amber-dark text-navy font-semibold px-8 h-14 text-lg">
                  Get a Free Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white px-8 h-14 text-lg">
                  View Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-navy py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
            {certifications.map((cert) => (
              <div key={cert.id} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal/20 flex items-center justify-center">
                  {cert.id === 1 && <Award className="text-amber" size={20} />}
                  {cert.id === 2 && <CheckCircle className="text-amber" size={20} />}
                  {cert.id === 3 && <Shield className="text-amber" size={20} />}
                </div>
                <span className="text-white font-medium">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4">
              Why Small Businesses Choose Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We understand the challenges of running a small business. Our bookkeeping services are designed to make your life easier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-teal/10 flex items-center justify-center mb-6 group-hover:bg-teal/20 transition-colors">
                    <benefit.icon className="text-teal" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-navy mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-6">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4">
                Our Bookkeeping Services
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl">
                From monthly bookkeeping to catch-up work, we offer comprehensive services tailored to your needs.
              </p>
            </div>
            <Link to="/services">
              <Button variant="outline" className="border-2 border-teal text-teal hover:bg-teal hover:text-white">
                View All Services
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-all bg-white group hover:-translate-y-1">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-navy mb-4 group-hover:text-teal transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="text-teal flex-shrink-0" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Preview */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-600">
              Don't just take our word for it—hear from businesses we've helped.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 2).map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg bg-slate-50">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-amber fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-slate-700 italic mb-6 leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p className="font-semibold text-navy">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.business}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/testimonials">
              <Button variant="outline" className="border-2 border-teal text-teal hover:bg-teal hover:text-white">
                Read More Reviews
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-teal/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-6">
            Ready to Get Your Finances Organised?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Let's have a chat about your bookkeeping needs. No obligation, no jargon—just honest advice about how we can help your business thrive.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessInfo.phone}`}>
              <Button size="lg" className="bg-amber hover:bg-amber-dark text-navy font-semibold px-8 h-14 text-lg w-full sm:w-auto">
                Call Us: {businessInfo.phone}
              </Button>
            </a>
            <a href={`mailto:${businessInfo.email}?subject=Bookkeeping%20Enquiry&body=Hello%20Wonderman%20Bookkeeping%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20bookkeeping%20services.%0A%0AName%3A%20%0ABusiness%20Name%3A%20%0APhone%3A%20%0A%0APlease%20get%20in%20touch%20at%20your%20earliest%20convenience.%0A%0AThank%20you.`}>
              <Button size="lg" variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white px-8 h-14 text-lg w-full sm:w-auto">
                Send a Message
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
